import React from 'react';
import { DietIdEnum, FormikRadioInput as Radio } from '@front-activerse/common-components';
import { useTranslator, useAppContext } from '@front-activerse/common-components';
import useSteps from '../StepContext/useSteps';
import { useFormikContext } from 'formik';
import { dietSettings } from '../types';
import { Row, Col } from 'reactstrap';

export const DietType: React.FC = () => {
  const { t, language } = useTranslator();
  const { dietTypes } = useSteps();
  const { values } = useFormikContext<dietSettings>();

  const {
    images: {
      dietTypeClassic,
      dietTypeClassicPt,
      dietTypeVege,
      dietTypeVegan,
      dietTypeGlp1,
      dietTypeLowIG,
      dietTypeSmart,
      dietTypeBudget,
      dietTypeHighProtein,
      dietTypeHighProteinVege,
      dietTypeComfort,
    },
  } = useAppContext();

  const enabledDietTypes = dietTypes?.filter((dietType) =>
    dietType.disableCondition ? !dietType.disableCondition(values) : true
  );
  const disabledDietTypes = dietTypes?.filter((dietType) =>
    dietType.disableCondition ? dietType.disableCondition(values) : false
  );

  const renderImage = (dietType: DietIdEnum) => {
    switch (dietType) {
      case DietIdEnum.DIET_ID_CLASSIC: {
        const isPortuguese = language.startsWith('pt');
        return (
          <img
            src={isPortuguese ? dietTypeClassicPt : dietTypeClassic}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeClassic"
          />
        );
      }
      case DietIdEnum.DIET_ID_VEGE:
        return (
          <img src={dietTypeVege} className="img-fluid mb-s border-radius-m" alt="dietTypeVege" />
        );
      case DietIdEnum.DIET_ID_LOW_IG:
        return (
          <img src={dietTypeLowIG} className="img-fluid mb-s border-radius-m" alt="dietTypeLowIG" />
        );
      case DietIdEnum.DIET_ID_SMART:
        return (
          <img src={dietTypeSmart} className="img-fluid mb-s border-radius-m" alt="dietTypeSmart" />
        );
      case DietIdEnum.DIET_ID_BUDGET:
        return (
          <img
            src={dietTypeBudget}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeBudget"
          />
        );
      case DietIdEnum.DIET_ID_HIGH_PROTEIN:
        return (
          <img
            src={dietTypeHighProtein}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeHighProtein"
          />
        );
      case DietIdEnum.DIET_ID_HIGH_PROTEIN_VEGE:
        return (
          <img
            src={dietTypeHighProteinVege}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeHighProteinVege"
          />
        );
      case DietIdEnum.DIET_ID_GLP1:
        return (
          <img src={dietTypeGlp1} className="img-fluid mb-s border-radius-m" alt="dietTypeGlp1" />
        );
      case DietIdEnum.DIET_ID_VEGAN:
        return (
          <img src={dietTypeVegan} className="img-fluid mb-s border-radius-m" alt="dietTypeVegan" />
        );
      case DietIdEnum.DIET_ID_COMFORT:
        return (
          <img
            src={dietTypeComfort}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeComfort"
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <h2 className="mb-4 title-large semi-bold">{t('app.goalSettings.dietType.headline')}</h2>

      {enabledDietTypes.map((dietType) => {
        if (!dietType.renderCondition || dietType.renderCondition(values)) {
          return (
            <Radio name="dietType" value={dietType.value} key={dietType.id}>
              {renderImage(dietType.value)}
              <strong>{t(dietType.optionTitle)}</strong>
              <p className="mb-0">{t(dietType.optionDescription)}</p>

              <Row className="mt-m">
                <Col>
                  <div className="d-flex align-items-start">
                    <div className="bullet protein"></div>
                    <div>
                      <p className="description-small mb-0">
                        {t('settings.dietType.macros.proteins')}
                      </p>
                      <p className="small mb-0">{dietType?.macro?.protein}%</p>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex align-items-start">
                    <div className="bullet fat"></div>
                    <div>
                      <p className="description-small mb-0">{t('settings.dietType.macros.fats')}</p>
                      <p className="small mb-0">{dietType?.macro?.fat}%</p>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex align-items-start">
                    <div className="bullet carbs"></div>
                    <div>
                      <p className="description-small mb-0">
                        {t('settings.dietType.macros.carbs')}
                      </p>
                      <p className="small mb-0">{dietType?.macro?.carbs}%</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Radio>
          );
        }

        return null;
      })}

      {disabledDietTypes && disabledDietTypes.length > 0 && (
        <>
          <hr className="my-l" />
          <p className="text-start black small">{t('nutritionSettings.notAvailable.headline')}</p>

          {disabledDietTypes.map((dietType) => (
            <Radio name="dietType" value={dietType.value} key={dietType.id} disabled>
              {renderImage(dietType.value)}
              <strong>{t(dietType.optionTitle)}</strong>
              <p className="mb-0">{t(dietType.optionDescription)}</p>

              <Row className="mt-m">
                <Col>
                  <div className="d-flex align-items-start">
                    <div className="bullet protein"></div>
                    <div>
                      <p className="description-small mb-0">
                        {t('settings.dietType.macros.proteins')}
                      </p>
                      <p className="small mb-0">{dietType?.macro?.protein}%</p>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex align-items-start">
                    <div className="bullet fat"></div>
                    <div>
                      <p className="description-small mb-0">{t('settings.dietType.macros.fats')}</p>
                      <p className="small mb-0">{dietType?.macro?.fat}%</p>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex align-items-start">
                    <div className="bullet carbs"></div>
                    <div>
                      <p className="description-small mb-0">
                        {t('settings.dietType.macros.carbs')}
                      </p>
                      <p className="small mb-0">{dietType?.macro?.carbs}%</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Radio>
          ))}
        </>
      )}

      <p className="description text-start mt-xxl mb-xxl text-text-variant-2-color">
        {t('nutritionSettings.dietType.macrosDescription')}
      </p>
    </>
  );
};

export default DietType;
